import { Injectable } from '@angular/core';

/**
 * Servicio de sanitización de mensajes SMS
 * Garantiza compatibilidad GSM-7 (160 caracteres) removiendo tildes, emojis y caracteres especiales
 * 
 * Sincronizado con backend: api-rendafin/src/modules/dripping/channels/sms/utils/sms-encoding.util.ts
 */
@Injectable({
  providedIn: 'root'
})
export class SmsEncodingService {
  /**
   * Mapa de reemplazos para normalizar caracteres especiales a GSM-7
   */
  private readonly CHAR_REPLACEMENTS: Record<string, string> = {
    // Vocales con tilde
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
    'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U',
    // Vocales con diéresis
    'ä': 'a', 'ë': 'e', 'ï': 'i', 'ö': 'o', 'ü': 'u',
    'Ä': 'A', 'Ë': 'E', 'Ï': 'I', 'Ö': 'O', 'Ü': 'U',
    // Ñ
    'ñ': 'n', 'Ñ': 'N',
    // Signos (remover versión inicial española)
    '¿': '', '¡': '',
    // Comillas tipográficas (escapadas)
    '\u201C': '"', '\u201D': '"',  // " "
    '\u2018': "'", '\u2019': "'",  // ' '
    '«': '"', '»': '"',
    // Guiones especiales
    '–': '-', '—': '-',
    // Emojis comunes -> texto
    '😊': ':)', '😃': ':)', '😄': ':)', '😁': ':D',
    '😈': '>:)', '😎': '8)', '😢': ':(', '😭': ':(', '😡': '>:(',
    '👋': '', '👍': '', '👎': '',
    '💰': '$', '💵': '$', '💴': '$', '💶': '$', '💷': '$',
    '✅': '[OK]', '❌': '[X]', '⚠️': '[!]', '⚠': '[!]',
    '📱': '', '📞': '', '💬': '', '📧': '', '🏦': '', '🏢': '',
    // Símbolos especiales
    '•': '*', '·': '*', '°': 'o', '×': 'x', '÷': '/',
  };

  /**
   * Sanitiza un texto removiendo tildes, emojis y caracteres especiales
   * Convierte el texto a formato GSM-7 compatible
   */
  sanitizeToGSM7(text: string): string {
    if (!text) return '';

    let sanitized = text;

    // Aplicar reemplazos
    for (const [original, replacement] of Object.entries(this.CHAR_REPLACEMENTS)) {
      sanitized = sanitized.replace(new RegExp(original, 'g'), replacement);
    }

    // Remover caracteres no ASCII (0-127)
    sanitized = sanitized.replace(/[^\x20-\x7E\n\r]/g, '');

    return sanitized;
  }

  /**
   * Trunca mensaje a 160 caracteres (1 SMS GSM-7)
   */
  truncateToSingleSms(text: string, maxLength: number = 160): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength);
  }

  /**
   * Sanitiza Y trunca mensaje (función principal)
   */
  sanitizeAndTruncate(text: string, maxLength: number = 160): string {
    const sanitized = this.sanitizeToGSM7(text);
    return this.truncateToSingleSms(sanitized, maxLength);
  }

  /**
   * Detecta si un texto fue modificado por la sanitización
   */
  wasSanitized(original: string): boolean {
    return original !== this.sanitizeToGSM7(original);
  }

  /**
   * Obtiene lista de caracteres que serían sanitizados
   */
  getIncompatibleChars(text: string): string[] {
    if (!text) return [];
    
    const incompatible = new Set<string>();
    for (const char of text) {
      if (this.CHAR_REPLACEMENTS[char] || 
          (char.charCodeAt(0) > 127 && char !== '\n' && char !== '\r')) {
        incompatible.add(char);
      }
    }
    return Array.from(incompatible);
  }
}
